import { useApp } from "@/app/AppContext";
import { api } from "@/auth/session";
import { useMutations } from "@/data/mutations";
import { useToast } from "@/ui/toast";
import type { FileKind, TrailRow } from "./queries";

// File-type tile, same language as the field-visit card's artifact chips.
const TILE: Record<FileKind, { label: string; cls: string }> = {
  pdf: { label: "PDF", cls: "ft--pdf" },
  report: { label: "RPT", cls: "ft--report" },
  image: { label: "IMG", cls: "ft--image" },
  sheet: { label: "XLS", cls: "ft--sheet" },
  doc: { label: "DOC", cls: "ft--doc" },
  other: { label: "FILE", cls: "ft--other" },
};

export function FileTile({ kind, size = 30 }: { kind: FileKind; size?: number }) {
  const t = TILE[kind];
  return <span className={`ft ${t.cls}`} style={{ width: size, height: size }} aria-hidden="true">{t.label}</span>;
}

export const KIND_LABEL: Record<FileKind, string> = { pdf: "PDFs", report: "Signed reports", image: "Images", sheet: "Spreadsheets", doc: "Docs", other: "Other" };

// Open a Paper Trail item: signed reports download through the report
// endpoint; attachments open a 15-minute signed URL (GET /api/v3/media).
export function useOpenFile() {
  const { mode } = useApp();
  const toast = useToast();
  const m = useMutations();
  return async (row: TrailRow) => {
    if (row.reportId) return m.downloadReport(row.reportId);
    if (mode === "demo" || !row.storageKey) {
      toast({ variant: "info", title: "Available with a live account", message: "Demo files have no stored document behind them." });
      return;
    }
    try {
      const r = await api<{ url: string }>(`/api/v3/media/${encodeURIComponent(row.storageKey)}?type=field_note_attachment`);
      window.open(r.url, "_blank", "noopener");
    } catch (e: any) {
      toast({ variant: "error", title: "File unavailable", message: e?.body?.error || "The signed link could not be created." });
    }
  };
}
